(ns app.front.ui.messages-panel
  "Global side panel that lists user's messages grouped by workspace.

   When opened outside a workspace: displays workspace search with filtering/sorting,
   lists up to 50 workspaces with most recent messages, and allows loading more.

   When opened within a workspace: displays conversations for that specific workspace,
   with an option to navigate back to all messages (workspace search)."
  (:require ["@carbon/icons-react/es/add/16" :default Add16]
            ["@carbon/icons-react/es/arrow--right/16" :default ArrowRight16]
            ["@carbon/icons-react/es/chevron--left/16" :default ChevronLeft16]
            ["@carbon/icons-react/es/launch/16" :default Launch16]
            ["@carbon/icons-react/es/location/16" :default Location16]
            ["@carbon/icons-react/es/renew/16" :default Renew16]
            ["@carbon/icons-react/es/reply/16" :default Reply16]
            ["@carbon/icons-react/es/subtract/16" :default Subtract16]
            ["@carbon/icons-react/es/user/24" :default User24]
            [app.domain.municipality.model :as municipality-model]
            [app.domain.party.model :as party-model]
            [app.domain.property.model :as property-model]
            [app.domain.user.authority :as authority]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.municipality :as municipality]
            [app.front.ui :as ui]
            [app.front.ui.content-switcher :as ui-content-switcher]
            [app.front.ui.panels :as panels]
            [app.front.ui.select :as ui-select]
            [app.front.ui.side-panel :as side-panel]
            [app.front.ui.text-button :as ui-text-button]
            [app.front.ui.viewport-observer :as viewport-observer]
            [app.front.ui.visual-viewport :as visual-viewport]
            [app.front.util :refer [for-frag tval]]
            [app.front.view.application.permissions :as permissions]
            [app.front.view.conversations.core :as conversation]
            [app.front.view.not-found :as not-found]
            [app.front.view.party.common :as party-common]
            [app.front.view.workspace.map :as workspace-map]
            [app.front.view.workspace.subs :as ws-subs]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.href :as href]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.string :as str-util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(def ^:private panel-id :messages)


(def ^:private page-size
  "Number of workspaces to fetch per page from the backend. The backend defaults
  to a much larger size for the legacy conversations view; this panel paginates
  via `Load more`, so we ask for a smaller batch."
  50)



(defn- ->equals-condition
  "Build a single `:filter.condition/equals` condition for a backend search."
  [attr value]
  {:filter.condition/attribute attr
   :filter.condition/operator  :equals
   :filter.condition/values    #{value}})


(defn- filter-values->conditions
  "Translate the panel's frontend `::filter-values` map into the backend
  `CommonSearchTerms` `:conditions` shape."
  [{:keys [department assignee]}]
  (cond-> []
    department (conj (->equals-condition :workspace/permit-type department))
    assignee   (conj (->equals-condition :workspace/assignee assignee))))


(defn- fetch-workspaces-fx
  "Build the `:dispatch` for fetching a page of workspaces using the panel's
  current search/filter state. Pass `:from` and `:size` explicitly."
  [{:keys [from search-query filter-values sort-condition]}]
  (let [conditions (filter-values->conditions filter-values)
        sort-by    (first sort-condition)]
    [:dispatch [::conversation/fetch-workspaces
                (cond-> {:from from
                         :size page-size}
                  sort-by                            (assoc :sort-by sort-by)
                  (not (str/blank? search-query))    (assoc :search-term search-query)
                  (seq conditions)                   (assoc :conditions conditions))]]))


(declare unseen-user-ids)
(declare recipient-selector)
(declare format-roles)


(rf/reg-sub ::panel-open?
  :<- [::panels/open? panel-id]
  :-> boolean)


(rf/reg-sub ::unread-count
  (fn [db _]
    (get db :app.front.root/conversation-notification-count 0)))


(rf/reg-sub ::state
  :-> ::state)


(rf/reg-sub ::search-query
  :<- [::state]
  :-> ::search-query)


(rf/reg-sub ::sort-condition
  :<- [::state]
  (fn [state]
    (get state ::sort-condition [:unread-first :desc])))


(rf/reg-sub ::selected-had-unread?
  :<- [::state]
  :-> ::selected-had-unread?)


(rf/reg-sub ::filters-expanded?
  :<- [::state]
  :-> ::filters-expanded?)


(rf/reg-sub ::filter-values
  :<- [::state]
  (fn [state]
    (get state ::filter-values {})))


(rf/reg-sub ::filters-active?
  :<- [::search-query]
  :<- [::filter-values]
  (fn [[search-query filter-values]]
    (or (not (str/blank? search-query))
        (seq filter-values))))


(def ^:private empty-mark-state
  {:workspace-id    nil
   :marked-ids      #{}
   :highlighted-ids #{}})


(rf/reg-sub ::view-mode
  :<- [::state]
  (fn [state]
    ;; :all-messages or :workspace-messages
    (get state ::view-mode :all-messages)))


(rf/reg-sub ::fullscreen?
  :<- [::state]
  (fn [state]
    ;; True while the full-screen messages view (:view/messages route) is
    ;; active. Shared components (e.g. workspace list and workspace header)
    ;; adapt their layout accordingly.
    (boolean (get state ::fullscreen?))))


(rf/reg-sub ::message-visibility
  :<- [::state]
  (fn [state]
    ;; :public or :private
    (get state ::message-visibility :public)))


(rf/reg-sub ::is-private?
  :<- [::message-visibility]
  (fn [visibility]
    (= visibility :private)))


(rf/reg-sub ::has-unread-private?
  :<- [::conversation/selected-workspace-list-item]
  (fn [ws-item]
    (boolean (seq (:private-not-seen ws-item)))))


(rf/reg-sub ::unread-in-other-workspaces?
  :<- [::workspace-list]
  :<- [::conversation/selected-workspace-id]
  (fn [[workspaces selected-workspace-id]]
    (boolean (some (fn [{:keys [not-seen private-not-seen workspace]}]
                     (and (not= selected-workspace-id (:workspace/id workspace))
                          (or (seq not-seen) (seq private-not-seen))))
                   workspaces))))


(rf/reg-sub ::selected-workspace
  :<- [::conversation/selected-workspace-id]
  :<- [::conversation/selected-workspace-list-item]
  :<- [::entities/context]
  (fn [[workspace-id list-item ctx]]
    ;; Prefer the full workspace entity from the entity-db (loaded via
    ;; `:workspace/get` when the workspace is selected), so we have the whole
    ;; graph — location, parties, categories, primary property, etc. Fall back
    ;; to the (attribute-limited) Elasticsearch list item while it loads.
    (or (some->> workspace-id
                 (hash-map :workspace/id)
                 (entities/find-entity ctx))
        (:workspace list-item))))


(rf/reg-sub ::selected-workspace-es
  ;; The Elasticsearch list item's workspace hit, which carries denormalized,
  ;; already-localized display fields (category/work-type/work-target names,
  ;; applicants) that are only present for users allowed to read them.
  :<- [::conversation/selected-workspace-list-item]
  :-> :workspace)


(rf/reg-sub ::selected-workspace-authority?
  :<- [:session/user]
  :<- [::selected-workspace]
  (fn [[user workspace]]
    (boolean
      (some->> workspace :domain/authority-group (authority/authority-in-group? user)))))


(rf/reg-sub ::selected-workspace-current-phase
  :<- [::selected-workspace]
  (fn [workspace]
    ;; The phase entity (application/execution/pre-consultation/...) that
    ;; matches the workspace's current state; used to look up parties/assignee.
    (case (:domain/state workspace)
      :workspace.state/application      (workspace-model/workspace-application workspace)
      :workspace.state/execution        (workspace-model/workspace-execution workspace)
      :workspace.state/pre-consultation (workspace-model/workspace-pre-consultation workspace)
      (workspace-model/workspace-application workspace))))


(defn- localized-names->str
  "Pick the display text for a list of `LocalizedStringExport` maps in the
   requested language (falling back to whatever language is available)."
  [names lang]
  (some-> names (i18n-model/get-text lang) str/trim str-util/blank->nil))


(rf/reg-sub ::selected-workspace-category
  :<- [::selected-workspace-es]
  :<- [:language]
  (fn [[es lang]]
    (localized-names->str (:workspace/category-names es) lang)))


(rf/reg-sub ::selected-workspace-work-type
  :<- [::selected-workspace-es]
  :<- [:language]
  (fn [[es lang]]
    (localized-names->str (:application/work-type-names es) lang)))


(rf/reg-sub ::selected-workspace-work-target
  :<- [::selected-workspace-es]
  :<- [:language]
  (fn [[es lang]]
    (localized-names->str (:application/work-target-names es) lang)))


(rf/reg-sub ::selected-workspace-creator-name
  :<- [::selected-workspace-current-phase]
  :<- [:language]
  (fn [[phase lang]]
    ;; Name of the party that created the workspace, used as a fallback when
    ;; there is no explicit applicant party.
    (some->> (party-model/phase-parties phase)
             (filter :party/workspace-creator?)
             (map #(party-model/simple-name % lang))
             (remove str/blank?)
             first)))


(rf/reg-sub ::selected-workspace-applicants
  :<- [::selected-workspace-es]
  :<- [::selected-workspace-current-phase]
  :<- [::selected-workspace-creator-name]
  :<- [:language]
  (fn [[es phase creator-name lang]]
    ;; Prefer the denormalized ES applicant names; otherwise resolve applicant
    ;; parties from the entity graph; finally fall back to the workspace
    ;; creator party if there is no applicant at all.
    (let [applicants (or (seq (:applicants es))
                         (seq (->> (party-model/active-parties-for-the-role phase :role/applicant)
                                   (map #(party-model/simple-name % lang))
                                   (remove str/blank?)
                                   distinct)))]
      (vec (or applicants
               (some-> creator-name vector))))))


(rf/reg-sub ::selected-workspace-map-layers
  :<- [::selected-workspace]
  :<- [::municipality/municipality-config]
  :<- [::municipality/map-tokens]
  (fn [[workspace config tokens]]
    ;; Municipal map layers keyed by the workspace's GIS customer /
    ;; domain-group id. Requires the map config to have been loaded for the
    ;; group (see the :view/messages route controller).
    (when-let [customer-id (workspace-model/customer-id workspace)]
      (municipality/municipal-layers config tokens customer-id false))))


(rf/reg-sub ::selected-workspace-map-center
  :<- [::selected-workspace]
  (fn [workspace]
    (let [location (or (:entity/location workspace)
                       (-> workspace :workspace/primary-property :entity/location)
                       (-> workspace :workspace/municipality :municipality/center-location))]
      (when location
        [(:location.center/x location)
         (:location.center/y location)]))))


(rf/reg-sub ::reply-thread-id
  :<- [::state]
  :-> ::reply-thread-id)


(rf/reg-sub ::last-replied-thread-id
  :<- [::state]
  :-> ::last-replied-thread-id)


(rf/reg-sub ::new-message-text
  :<- [::state]
  (fn [state [_ thread-id]]
    (get-in state [::new-message-text thread-id] "")))


;; Read raw conversation data directly from state, bypassing the re-sorting in
;; ::conversation/conversation-data. The backend already returns items in the
;; correct order, and re-sorting on append would cause scroll jumps by
;; interleaving new items with existing ones.
;; For :unread-first, we apply a stable partition (no re-sort within groups) to
;; reflect local unread state changes (e.g. after mark-as-read).
(rf/reg-sub ::workspace-list
  (fn [db _]
    (get-in db [::conversation/state ::conversation/conversation-data])))

(rf/reg-sub ::workspace-list-sorted
  :<- [::workspace-list]
  :<- [::sort-condition]
  :<- [::conversation/selected-workspace-id]
  :<- [::selected-had-unread?]
  (fn [[data [sort-attr _sort-dir] selected-ws-id selected-had-unread?]]
    (if (= sort-attr :unread-first)
      ;; Keep the currently open workspace in the "unread" group even after its
      ;; messages get marked as seen, so it doesn't jump down the list while the
      ;; user is reading it. It settles into its natural position only once a
      ;; different workspace is opened. Only pin workspaces that actually had
      ;; unread messages when opened — a workspace opened from the read group
      ;; must not jump up into the unread group.
      (let [pin-id      (when selected-had-unread? selected-ws-id)
            has-unread? (fn [{:keys [not-seen private-not-seen] :as item}]
                          (or (= pin-id (-> item :workspace :workspace/id))
                              (seq not-seen)
                              (seq private-not-seen)))]
        (concat (filter has-unread? data)
                (remove has-unread? data)))
      data)))


(rf/reg-sub ::total-count
  :<- [::conversation/conversation-total-count]
  identity)


(rf/reg-sub ::loading?
  :<- [::conversation/loading?]
  identity)


(rf/reg-sub ::can-load-more?
  :<- [::conversation/can-load-more?]
  identity)


(rf/reg-sub ::current-count
  :<- [::conversation/current-workspace-count]
  identity)


(rf/reg-sub ::current-workspace-item
  :<- [::ws-subs/workspace]
  :<- [::conversation/conversation-data]
  (fn [[workspace data]]
    (when-let [ws-id (:workspace/id workspace)]
      (or (some #(when (= ws-id (-> % :workspace :workspace/id)) %)
                data)
          {:workspace workspace}))))


(rf/reg-sub ::other-workspaces
  :<- [::workspace-list-sorted]
  :<- [::ws-subs/workspace]
  (fn [[workspaces current-ws]]
    (if-let [ws-id (:workspace/id current-ws)]
      (remove #(= ws-id (-> % :workspace :workspace/id)) workspaces)
      workspaces)))


(rf/reg-sub ::listed-count
  :<- [::other-workspaces]
  :<- [::current-workspace-item]
  (fn [[other-workspaces current-ws-item]]
    (+ (count other-workspaces)
       (if current-ws-item 1 0))))


(rf/reg-sub ::mark-state
  :<- [::state]
  (fn [state]
    (get state ::mark-state empty-mark-state)))


(rf/reg-sub ::highlighted-message-ids
  :<- [::mark-state]
  :-> :highlighted-ids)


(rf/reg-sub ::display-current-count
  :<- [::filters-active?]
  :<- [::listed-count]
  :<- [::current-count]
  (fn [[filters-active? listed-count current-count]]
    (if filters-active?
      listed-count
      current-count)))


(rf/reg-sub ::display-total-count
  :<- [::filters-active?]
  :<- [::listed-count]
  :<- [::total-count]
  (fn [[filters-active? listed-count total-count]]
    (if filters-active?
      listed-count
      total-count)))


(rf/reg-sub ::current-user-id
  :<- [:session/user-id]
  identity)


(rf/reg-sub ::is-admin?
  :<- [:session/admin?]
  identity)


(rf/reg-sub ::workspace-threads
  :<- [::is-private?]
  :<- [::conversation/workspace-conversation-threads false]
  :<- [::conversation/workspace-conversation-threads true]
  (fn [[is-private? public-threads private-threads]]
    (if is-private?
      private-threads
      public-threads)))


(rf/reg-sub ::direct-conversation-threads
  :<- [::conversation/workspace-conversation-threads true]
  :<- [::conversation/selected-direct-message-participants]
  (fn [[threads participants]]
    (get (->> threads
              (group-by #(->> % :conversation-thread/users vec (map :db/id) set)))
         participants
         [])))




(rf/reg-sub ::department-options
  (fn [db _]
    (->> (get-in db [::state ::filter-options :departments])
         sort)))


(rf/reg-sub ::assignee-options
  (fn [db _]
    (->> (get-in db [::state ::filter-options :assignees])
         (map (fn [assignee]
                {:id   (:db/id assignee)
                 :name (user-model/full-name assignee)}))
         (sort-by :name))))


(rf/reg-sub ::can-participate?
  :<- [::entities/context]
  :<- [::entities/entity-db]
  :<- [::conversation/selected-workspace-id]
  (fn [[ctx db workspace-id]]
    (when-let [workspace (entities/find-entity ctx {:workspace/id workspace-id})]
      (permissions/can-send-workspace-messages? (:db/id workspace) (vals db)))))


(rf/reg-sub ::participating?
  :<- [::conversation/selected-direct-message-participants]
  :<- [::current-user-id]
  (fn [[participants current-user-id]]
    (contains? (set participants) current-user-id)))


(rf/reg-event-db ::set-fullscreen
  (fn [db [_ fullscreen?]]
    (assoc-in db [::state ::fullscreen?] (boolean fullscreen?))))


(rf/reg-event-fx ::ensure-workspace-map-config
  (fn [{:keys [db]} [_ workspace]]
    ;; Load the group's GIS map config (layers/tokens) once so the summary
    ;; sidebar can render a small map. No-op if it is already loaded.
    (let [customer-id (workspace-model/customer-id workspace)]
      (when (and customer-id
                 (not (get-in db [::municipality/municipality-config customer-id])))
        {:dispatch [::municipality/load-map-config customer-id]}))))


(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ current-workspace-id]]
    (let [search-query   (get-in db [::state ::search-query])
          filter-values  (get-in db [::state ::filter-values])
          sort-condition (get-in db [::state ::sort-condition] [:unread-first :desc])
          fetch-fx       (fetch-workspaces-fx {:from           0
                                               :search-query   search-query
                                               :filter-values  filter-values
                                               :sort-condition sort-condition})
          options-fx     [:dispatch [::fetch-filter-options]]
          workspace?     (some->> current-workspace-id
                                  (hash-map :workspace/id)
                                  (entities/find-entity (entities/db->context db)))
          workspace-fx   (when (and current-workspace-id (not workspace?))
                           [:dispatch [::conversation/fetch-workspace-entities current-workspace-id]])]
      (if current-workspace-id
        ;; If inside a workspace, show workspace messages view
        {:fx (cond-> [[:dispatch [::conversation/set-selected-workspace-id current-workspace-id]]
                      [:dispatch [::conversation/fetch-workspace-messages current-workspace-id]]
                      fetch-fx
                      options-fx]
               workspace-fx (conj workspace-fx))
         :db (-> db
                 (assoc-in [::state ::view-mode] :workspace-messages)
                 (assoc-in [::state ::message-visibility] :public))}
        ;; If not in a workspace, show all messages view
        {:fx [fetch-fx
              options-fx]
         :db (-> db
                 (assoc-in [::state ::view-mode] :all-messages)
                 (assoc-in [::state ::message-visibility] :public))}))))


(rf/reg-event-fx ::fetch-filter-options
  (fn [_ _]
    {:comm/command {:command    :conversation/get-workspace-filter-options
                    :method     :get
                    :on-success [::set-filter-options]}}))


(rf/reg-event-db ::set-filter-options
  (fn [db [_ status {:keys [departments assignees]}]]
    (if (= status 200)
      (assoc-in db [::state ::filter-options]
                {:departments departments
                 :assignees   assignees})
      db)))


(rf/reg-event-fx ::toggle-open
  (fn [{:keys [db]} [_ current-workspace-id]]
    (let [currently-open? (= (-> db :ui/open-panel first) panel-id)]
      {:fx (concat [[:dispatch [::panels/toggle panel-id]]]
                   (when-not currently-open?
                     [[:dispatch [::init current-workspace-id]]]))})))


(rf/reg-event-fx ::search
  (fn [{:keys [db]} [_ term]]
    (let [filter-values  (get-in db [::state ::filter-values])
          sort-condition (get-in db [::state ::sort-condition] [:unread-first :desc])
          fetch-event    (second (fetch-workspaces-fx {:from           0
                                                       :search-query   term
                                                       :filter-values  filter-values
                                                       :sort-condition sort-condition}))]
      {:dispatch-debounce {:id      ::do-search
                           :timeout 400
                           :action  :dispatch
                           :event   fetch-event}
       :db                (assoc-in db [::state ::search-query] term)})))


(rf/reg-event-fx ::set-sort-condition
  (fn [{:keys [db]} [_ value]]
    (let [db'           (-> db
                            (assoc-in [::state ::sort-condition] value)
                            (assoc-in [::conversation/state ::conversation/conversation-data] nil)
                            (update ::conversation/state dissoc ::conversation/conversation-total-count))
          search-query  (get-in db' [::state ::search-query])
          filter-values (get-in db' [::state ::filter-values])]
      {:db db'
       :fx [(fetch-workspaces-fx {:from           0
                                  :search-query   search-query
                                  :filter-values  filter-values
                                  :sort-condition value})]})))


(rf/reg-event-db ::toggle-filters
  (fn [db _]
    (update-in db [::state ::filters-expanded?] not)))


(rf/reg-event-fx ::set-filter-value
  (fn [{:keys [db]} [_ attr value]]
    (let [db'            (if (some? value)
                           (assoc-in db [::state ::filter-values attr] value)
                           (p/dissoc-in db [::state ::filter-values attr]))
          search-query   (get-in db' [::state ::search-query])
          filter-values  (get-in db' [::state ::filter-values])
          sort-condition (get-in db' [::state ::sort-condition] [:unread-first :desc])]
      {:db db'
       :fx [(fetch-workspaces-fx {:from           0
                                  :search-query   search-query
                                  :filter-values  filter-values
                                  :sort-condition sort-condition})]})))


(rf/reg-event-fx ::clear-filters
  (fn [{:keys [db]} _]
    (let [db'            (-> db
                             (assoc-in [::state ::filter-values] {})
                             (assoc-in [::state ::filters-expanded?] false))
          search-query   (get-in db' [::state ::search-query])
          sort-condition (get-in db' [::state ::sort-condition] [:unread-first :desc])]
      {:db db'
       :fx [(fetch-workspaces-fx {:from           0
                                  :search-query   search-query
                                  :filter-values  {}
                                  :sort-condition sort-condition})]})))


(rf/reg-event-fx ::set-message-visibility
  (fn [{:keys [db]} [_ visibility]]
    {:db       (assoc-in db [::state ::message-visibility] visibility)
     :dispatch [::conversation/set-direct-message-participants nil]}))


(rf/reg-event-db ::set-reply-thread-id
  (fn [db [_ thread-id]]
    (assoc-in db [::state ::reply-thread-id] thread-id)))


(rf/reg-event-db ::set-last-replied-thread-id
  (fn [db [_ thread-id]]
    (assoc-in db [::state ::last-replied-thread-id] thread-id)))


(rf/reg-event-db ::set-new-message-text
  (fn [db [_ thread-id text]]
    (assoc-in db [::state ::new-message-text thread-id] text)))


(rf/reg-event-db ::reset-mark-state
  (fn [db _]
    (assoc-in db [::state ::mark-state] empty-mark-state)))


(rf/reg-event-fx ::mark-rendered-messages-seen
  (fn [{:keys [db]} [_ workspace-id conversation-ids current-user-id]]
    (when workspace-id
      (let [{stored-ws-id :workspace-id
             marked-ids   :marked-ids
             :or          {marked-ids #{}}} (get-in db [::state ::mark-state] empty-mark-state)
            reset?                          (not= stored-ws-id workspace-id)
            marked-ids-display              (if reset? #{} marked-ids)
            conversation-ids-set            (or conversation-ids #{})
            new-ids                         (set/difference conversation-ids-set marked-ids-display)
            entity-db                       (-> db ::entities/entity-db)
            new-highlighted-ids             (->> new-ids
                                                 (filter (fn [id]
                                                           (contains? (unseen-user-ids (get entity-db id)) current-user-id)))
                                                 (into #{}))
            mark-state-base                 (if reset?
                                              (assoc empty-mark-state :workspace-id workspace-id)
                                              (assoc (get-in db [::state ::mark-state] empty-mark-state)
                                                     :workspace-id workspace-id))
            mark-state                      (cond-> mark-state-base
                                              (seq new-highlighted-ids)
                                              (update :highlighted-ids (fnil into #{}) new-highlighted-ids)
                                              (seq new-ids)
                                              (update :marked-ids (fnil into #{}) new-ids))]
        (cond-> {:db (assoc-in db [::state ::mark-state] mark-state)}
          (seq new-ids)
          (assoc :dispatch [::conversation/set-conversations-seen-batch new-ids]))))))


(rf/reg-event-fx ::select-workspace
  (fn [{:keys [db]} [_ workspace-id]]
    (let [item        (->> (get-in db [::conversation/state ::conversation/conversation-data])
                           (some #(when (= workspace-id (-> % :workspace :workspace/id)) %)))
          had-unread? (boolean (or (seq (:not-seen item))
                                   (seq (:private-not-seen item))))]
      {:dispatch-n [[::conversation/set-selected-workspace-id workspace-id]
                    [::conversation/fetch-workspace-messages workspace-id]
                    [::conversation/fetch-workspace-entities workspace-id]]
       :db         (-> db
                       (assoc-in [::state ::view-mode] :workspace-messages)
                       (assoc-in [::state ::message-visibility] :public)
                       ;; Remember whether this workspace had unread messages when
                       ;; opened. Only such a workspace is pinned in the unread
                       ;; group so it doesn't jump down once marked as seen; a
                       ;; workspace opened from the read group must not jump up.
                       (assoc-in [::state ::selected-had-unread?] had-unread?))})))


(rf/reg-event-fx ::back-to-all-messages
  (fn [{:keys [db]} _]
    {:dispatch [::conversation/set-selected-workspace-id nil]
     :db       (assoc-in db [::state ::view-mode] :all-messages)}))


(rf/reg-event-fx ::load-more
  (fn [{:keys [db]} _]
    (let [conversation-data (get-in db [::conversation/state
                                        ::conversation/conversation-data])
          current-count     (count conversation-data)
          search-query      (get-in db [::state ::search-query])
          filter-values     (get-in db [::state ::filter-values])
          sort-condition    (get-in db [::state ::sort-condition] [:unread-first :desc])]
      {:fx [(fetch-workspaces-fx {:from           current-count
                                  :search-query   search-query
                                  :filter-values  filter-values
                                  :sort-condition sort-condition})]})))


(def sort-options
  [[:unread-first :desc]            ;; unread messages first (default)
   [:last-conversation-date :desc]]) ;; most recent conversation activity


(def ^:private form-stack-div :div.flex.flex-col.gap-2)
(def ^:private form-field-div :div.flex.flex-col)
(def ^:private label-sm :label.text-sm.text-ui-night-sky-100)
(def ^:private phase-label :div.flex.items-center.justify-center.py-1.px-4.border.border-solid.border-gray-400.bg-white.rounded.font-bold.text-sm.text-black)
(def ^:private unread-dot :div.w-2.h-2.rounded-full.bg-ui-red.shrink-0)


(defn- search-input
  "Search input with label above"
  [tr]
  (let [query (<sub [::search-query])]
    [form-stack-div
     ;; Label
     [label-sm
      {:for "messages-panel-search"}
      (tr [:messages/find-workspace])]
     ;; Input field with gray background and bottom border
     [form-field-div
      [ui/search {:id                      "messages-panel-search"
                  :label-text              (tr [:messages/find-workspace])
                  :hide-label              true
                  :close-button-label-text (tr [:search/empty])
                  :size                    :medium
                  :icon-position           :right
                  :show-clear?             false
                  :value                   (or query "")
                  :placeholder             (tr [:messages/workspace-search-placeholder])
                  :container-class         "bg-gray-200 rounded"
                  :class                   "!bg-transparent !outline-none !px-4 !pr-10 text-sm text-ui-night-sky-100 placeholder:text-ui-night-sky-70"
                  :on-change               #(>evt [::search (tval %)])}]]]))


(defn- sort-select
  "Sort dropdown - gray background and bottom border"
  [tr]
  (let [current (<sub [::sort-condition])]
    [form-field-div
     [ui-select/select {:id           "messages-panel-sort"
                        :class-name   "w-full"
                        :allow-empty? false
                        :select-text  (tr [:messages/sort])
                        :values       sort-options
                        :value        current
                        :on-change    #(>evt [::set-sort-condition %])
                        :val->str     (fn [[attr direction]]
                                        (tr [(keyword "search-sorting"
                                                      (str (name attr) "-" (name direction)))]))}]]))


(defn- filter-button
  "Filter workspaces button - uppercase purple text with plus icon.
   Hidden when filters are expanded."
  [tr]
  (let [expanded? (<sub [::filters-expanded?])]
    (when-not expanded?
      [ui/button {:kind       :tertiary
                  :size       :field
                  :on-click   #(>evt [::toggle-filters])
                  :class-name "w-full !justify-center gap-2"}
       (tr [:messages/filter-workspaces])
       [:> Add16]])))


(defn- filter-dropdown
  "Filter dropdown with label"
  [tr attr label placeholder options]
  (let [filter-values        (<sub [::filter-values])
        selected             (get filter-values attr)
        id                   (str "messages-panel-filter-" (name attr))
        label-id             (str id "-label")
        {values   :values
         val->str :val->str} (case attr
                               :assignee
                               (let [id->name (into {} (map (juxt :id :name) options))]
                                 {:values   (mapv :id options)
                                  :val->str #(get id->name % "")})

                               :department
                               {:values   options
                                :val->str (fn [permit-type]
                                            (if (keyword? permit-type)
                                              (tr [permit-type])
                                              (str permit-type)))}

                               {:values   options
                                :val->str str})]
    [form-field-div
     ;; Label
     [:div.pb-2
      [:span.text-sm.text-ui-night-sky-100 {:id label-id} label]]
     ;; Dropdown field
     [form-field-div
      [ui-select/select {:id              id
                         :class-name      "w-full"
                         :allow-empty?    true
                         :empty-text      placeholder
                         :values          values
                         :value           selected
                         :on-change       #(>evt [::set-filter-value attr %])
                         :val->str        val->str
                         :aria-labelledby label-id
                         :external-label? true}]]]))


(defn- filters-section
  "Expandable filters section with bordered container"
  [tr]
  (let [expanded?          (<sub [::filters-expanded?])
        department-options (<sub [::department-options])
        assignee-options   (<sub [::assignee-options])]
    (when expanded?
      [:div.p-2.border.border-solid.border-gray-400.rounded.flex.flex-col.gap-2
       ;; Department dropdown
       [filter-dropdown
        tr
        :department
        (tr [:messages/department])
        (tr [:messages/select-department])
        department-options]

       ;; Assignee dropdown
       [filter-dropdown
        tr
        :assignee
        (tr [:messages/assignee])
        (tr [:messages/select-assignee])
        assignee-options]

       ;; Clear filters button
       [ui/button {:kind       :primary
                   :size       :field
                   :on-click   #(>evt [::clear-filters])
                   :class-name "w-full !justify-center gap-2"}
        (tr [:messages/clear-filters])
        [:> Subtract16 {:class "text-ui-white"}]]])))


(defn- divider
  "Horizontal divider line"
  []
  [:div.w-full.h-px.bg-gray-400])


(defn- left-stripe
  "Left colored stripe indicating unread status - matches collapsible pattern"
  [has-unread? selected?]
  [:div.w-1.5.self-stretch.shrink-0.rounded-tl.rounded-bl.-my-px.-ml-px
   {:class (cond
             has-unread? "bg-ui-orange"
             selected? "bg-ui-violet-100"
             :else "bg-gray-300")}])


(defn- workspace-card
  "Workspace card with orange/gray left border indicator for unread/read state.
   Design details:
   - 6px left stripe (orange for unread, gray for read)
   - Gray background (#ECEDEF)
   - 1px border (#B3B9BE)
   - Address in 14px bold
   - Description in 12px regular (permit type)
   - Message counts in 12px regular"
  [{:keys [workspace not-seen private-not-seen]} tr animated?]
  (let [ws-id          (:workspace/id workspace)
        selected-ws-id (<sub [::conversation/selected-workspace-id])
        selected?      (= ws-id selected-ws-id)
        address        (or (:workspace/address workspace)
                           (-> workspace :property/id first))
        permit-type    (:workspace/permit-type workspace)
        permit-label   (tr [permit-type])
        private-unread (count private-not-seen)
        total-unread   (count (or not-seen #{}))
        public-unread  (max 0 (- total-unread private-unread))
        has-unread?    (pos? total-unread)]
    [:button.flex.rounded.cursor-pointer.mb-2.border.border-solid.overflow-hidden.appearance-none.p-0.w-full.text-left
     {:class        [(if selected?
                       "border-ui-violet-100 bg-ui-violet-10 hover:bg-ui-violet-10"
                       "border-gray-400 bg-gray-200 hover:bg-gray-100")
                     (when animated? "animate-appear-slide-in-up-200")]
      :aria-current (when selected? "true")
      :on-click     #(>evt [::select-workspace ws-id])}
     ;; Left stripe for unread indicator
     [left-stripe has-unread? selected?]
     ;; Card content
     [:div.flex-1.py-2.px-4
      ;; Address - 14px bold
      [:div.font-bold.text-sm.text-ui-night-sky-100.leading-tight
       (or address ws-id)]
      ;; Description - 12px regular (permit type)
      (when permit-label
        [:div.text-xs.text-ui-night-sky-100.mt-2.leading-tight
         permit-label])
      ;; Message counts - only show if there are unread messages
      (when has-unread?
        [:div.flex.flex-col.gap-2.mt-2
         (when (pos? private-unread)
           [:div.flex.items-center.gap-1.text-xs.text-ui-night-sky-100
            [unread-dot]
            (tr [:messages/n-new-private-messages] [private-unread])])
         (when (pos? public-unread)
           [:div.flex.items-center.gap-1.text-xs.text-ui-night-sky-100
            [unread-dot]
            (tr [:messages/n-new-public-messages] [public-unread])])])]]))


(defn- info-box
  "Info box showing current count of total"
  [tr current-count total-count]
  [:div.p-2.border.border-solid.border-gray-400.rounded.text-xs.text-ui-night-sky-100.leading-tight
   (tr [:messages/showing-of-total] [current-count total-count])])


(defn- this-workspace-section
  "Section showing the current workspace when viewing from within a workspace"
  [tr current-ws-item]
  (when current-ws-item
    [:div.flex.flex-col.gap-2.mb-4
     [:span.text-xs.text-ui-night-sky-100
      (tr [:messages/this-workspace])]
     [workspace-card current-ws-item tr false]]))


(defn- workspace-list-view
  "List of workspaces with messages. Preserves scroll position and animates
  newly appended items when loading more."
  [tr]
  (r/with-let [*prev-count (volatile! 0)]
    (let [current-ws-item  (<sub [::current-workspace-item])
          other-workspaces (<sub [::other-workspaces])
          loading?         (<sub [::loading?])
          current-count    (<sub [::display-current-count])
          total-count      (<sub [::display-total-count])
          prev-count       @*prev-count
          item-count       (count other-workspaces)]
      ;; Schedule prev-count update after the DOM has painted so the animation
      ;; class is applied on this render and the browser has time to start it.
      (when (and (pos? item-count) (not= item-count prev-count))
        (js/requestAnimationFrame #(vreset! *prev-count item-count)))
      [:div.flex.flex-col.gap-2
       ;; This Workspace section (only shown when inside a workspace)
       [this-workspace-section tr current-ws-item]

       ;; Section header with count
       (when-not loading?
         [:span.text-xs.text-ui-night-sky-100
          (tr [:messages/workspaces-found] [total-count])])

       ;; Info box showing pagination info
       (when (and current-count total-count (pos? total-count))
         [info-box tr current-count total-count])

       (when (pos-int? (<sub [::unread-count]))
         [ui-text-button/text-button
          {:on-click #(>evt [::conversation/set-all-conversations-seen])}
          (tr [:conversation/mark-all-as-read])])

       ;; Workspace list (excluding current workspace)
       [:div.flex-1
        (cond
          loading?
          [:div.flex.items-center.justify-center.py-8
           (cc/inline-loading {:description (tr [:messages/loading])})]

          (empty? other-workspaces)
          [:div.text-center.py-8.text-ui-night-sky-70
           (tr [:messages/no-workspaces])]

          :else
          (doall
            (map-indexed
              (fn [idx {:keys [workspace] :as item}]
                ^{:key (:workspace/id workspace)}
                [workspace-card item tr (and (pos? prev-count) (>= idx prev-count))])
              other-workspaces)))]])))


(defn- load-more-button
  "Load more button - uppercase text with refresh icon"
  [tr]
  [:div.flex.justify-center.pt-2.pb-6
   [ui/button {:kind       :tertiary
               :size       :field
               :on-click   #(>evt [::load-more])
               :class-name "gap-2"}
    (tr [:messages/load-more-btn])
    [:> Renew16]]])


(defn all-messages-view
  "Main view showing all workspaces with messages"
  [tr]
  (let [can-load-more? (<sub [::can-load-more?])]
    [:div.flex.flex-col.h-full.gap-2
     [search-input tr]
     [sort-select tr]
     [filter-button tr]
     [filters-section tr]
     [:div.my-2
      [divider]]
     [workspace-list-view tr]
     (if can-load-more?
       [load-more-button tr]
       [:div.min-h-4])]))


(defn- panel-back-button
  "Shared back-navigation button with chevron icon"
  [on-click label]
  [ui/button {:kind       :tertiary
              :size       :field
              :on-click   on-click
              :class-name "!justify-start !text-left !bg-transparent !border-0 !px-0 !py-2 !min-h-0 gap-2 text-xs font-bold !text-ui-night-sky-100 hover:underline focus:underline"}
   [:> ChevronLeft16]
   label])


(defn- panel-send-button
  "Shared violet send button with arrow icon and loading indicator"
  [tr disabled? on-click pending?]
  [:<>
   [ui/button {:kind       :primary
               :size       :field
               :type       :button
               :aria-label (tr [:conversation/send-message])
               :disabled   disabled?
               :on-click   (fn [e]
                             (when-not disabled?
                               (on-click e)))
               :class-name (str "w-11 h-11 !p-0 rounded !justify-center !items-center !flex !bg-ui-violet-100 !border-ui-violet-100"
                                (if disabled?
                                  " opacity-50 cursor-not-allowed"
                                  " hover:!bg-ui-violet-130"))}
    (if pending?
      [:div.-mr-2 (cc/inline-loading {})]
      [:> ArrowRight16 {:class "text-ui-white"}])]])


(defn- open-full-screen-button
  "Small button in the panel header that navigates to the full-screen
   messages view and closes the panel."
  [tr]
  (when (ff/enabled? :feature-flag.id/full-screen-messages)
    (let [selected-ws-id (<sub [::conversation/selected-workspace-id])
          href           (if selected-ws-id
                           (href/href :view/messages nil {:workspace-id selected-ws-id})
                           (href/href :view/messages))]
      [:a.hidden.lg:flex.items-center.gap-1.text-xs.font-normal.uppercase.text-ui-night-sky-100.no-underline.hover:underline.focus:underline.py-1
       {:href     href
        :title    (tr [:messages/full-screen])
        :on-click (fn [_] (>evt [::panels/toggle panel-id]))}
       [:span (tr [:messages/full-screen])]
       [:> Launch16]])))


(defn- workspace-header
  "Workspace header - shows address and municipality info"
  [tr workspace]
  (let [address      (or (:workspace/address workspace)
                         (-> workspace :property/id first))
        lang         (<sub [:language])
        fullscreen?  (<sub [::fullscreen?])
        municipality (municipality-model/common-name-province workspace tr lang)]
    [:div.flex.flex-row.lg:flex-col.items-center.lg:items-start.gap-6.lg:gap-0.justify-between.-mt-2.lg:mt-0
     ;; In the full-screen view the workspace list is always visible, so the
     ;; "All messages" back button is not relevant.
     (when-not fullscreen?
       [:div.flex.flex-row.items-center.self-stretch
        [panel-back-button #(>evt [::back-to-all-messages])
         [:span.flex.flex-row.items-center.gap-1
          (tr [:messages/all-messages])
          (when (<sub [::unread-in-other-workspaces?])
            [unread-dot {:class "mb-px"}])]]
        [:div.flex-1]
        [open-full-screen-button tr]])
     [:a.flex.flex-row.gap-1.no-underline.hover:underline.py-2.text-ui-night-sky-100.gap-2.items-center
      {:href (href/href :view/workspace {:workspace-id (:workspace/id workspace)})}
      [:> Location16]
      [:span.flex.flex-col.flex-1.justify-center
       ;; Address - 14px bold
       [:span.text-xs (or address (:workspace/id workspace))]
       ;; Municipality - 12px regular
       (when municipality [:span.text-xs municipality])]]]))


(defn- reset-textarea-height!
  "Reset textarea and its parent container to auto height after sending."
  [textarea-id]
  (when-let [textarea (.getElementById js/document textarea-id)]
    (set! (.-height (.-style textarea)) "auto")
    (when-let [parent (.-parentElement textarea)]
      (set! (.-height (.-style parent)) "auto"))))


(defn- auto-resize-textarea
  "Auto-resize textarea to fit content. Reads scrollHeight after resetting
   to auto so that the textarea shrinks when lines are deleted.
   Caps height at max-textarea-height-px to match the CSS max-h constraint."
  [e]
  (let [textarea               (.-target e)
        parent-el              (.-parentElement textarea)
        max-textarea-height-px 120]
    (js/setTimeout (fn []
                     (set! (.-height (.-style textarea)) "auto")
                     (let [scroll-h (+ (.-scrollHeight textarea) 2)
                           capped   (min scroll-h max-textarea-height-px)
                           height   (str capped "px")]
                       (set! (.-height (.-style textarea)) height)
                       (set! (.-height (.-style parent-el)) height))) 1)))


(defn- reply-input
  "Reply input for a specific thread. Stays visible with a loading indicator
   while the message is being sent and closes automatically on success.
   Smoothly scrolls itself into view when opened."
  [_tr _thread-id _workspace-id _*scroll-ref]
  (let [*sent?     (atom false)
        *mounted?  (atom false)
        scroll-ref (fn [el *scroll-ref]
                     (when (and el (not @*mounted?) @*scroll-ref)
                       (reset! *mounted? true)
                       (let [scroll-el         @*scroll-ref
                             scroll-to-center! (fn []
                                                 (let [el-top     (- (.-offsetTop el) (.-offsetTop scroll-el))
                                                       el-center  (+ el-top (/ (.-offsetHeight el) 2))
                                                       viewport-h (.-clientHeight scroll-el)]
                                                   (.scrollTo scroll-el #js {:top      (max 0 (- el-center (/ viewport-h 2)))
                                                                             :behavior "smooth"})))]
                         (scroll-to-center!)
                         ;; On mobile, the keyboard will appear (due to auto-focus) and the
                         ;; panel will resize. Re-scroll once the viewport has settled.
                         (visual-viewport/on-shrink-once
                           (fn [] (js/setTimeout scroll-to-center! 150))))
                       nil))]
    (fn [tr thread-id workspace-id *scroll-ref]
      (let [message          (<sub [::new-message-text thread-id])
            pending?         (<sub [::conversation/message-send-pending?])
            _                (when (and @*sent? (not pending?))
                               (reset! *sent? false)
                               (r/after-render #(>evt [::set-reply-thread-id nil])))
            can-participate? (<sub [::can-participate?])
            inactive?        (<sub [::conversation/direct-conversation-inactive?])
            disabled?        (or (str/blank? message) pending? (not can-participate?) inactive?)
            textarea-id      (str "messages-panel-reply-" thread-id)
            send-message!    (fn [e]
                               (.preventDefault e)
                               (when-not pending?
                                 (reset! *sent? true)
                                 (>evt [::conversation/add-new-message message workspace-id thread-id nil nil])
                                 (>evt [::set-new-message-text thread-id ""])
                                 (>evt [::set-last-replied-thread-id thread-id])
                                 (reset-textarea-height! textarea-id)))]
        [:div.flex.flex-col.gap-1.pl-5
         {:ref #(scroll-ref % *scroll-ref)}
         [:div.relative.flex.items-start.gap-2
          [:div.textarea-container.self-stretch.flex-1
           [ui/text-area {:id          textarea-id
                          :label-text  (tr [:conversation/reply])
                          :hide-label  true
                          :rows        1
                          :value       (or message "")
                          :auto-focus  true
                          :placeholder (tr [:conversation/reply])
                          :light       true
                          :class       "self-stretch"
                          :input-class (str "flex-1 !min-h-11 !px-2 !text-ui-night-sky-100"
                                            " placeholder:!text-ui-night-sky-100 text-base"
                                            " border border-solid border-gray-400 rounded"
                                            " resize-none overflow-y-auto max-h-[7.5rem]")
                          :on-change   #(>evt [::set-new-message-text thread-id (tval %)])
                          :on-key-up   auto-resize-textarea
                          :on-blur     auto-resize-textarea
                          :on-key-down (conversation/get-quick-send-handler send-message! disabled?)
                          :key         workspace-id}]]
          [panel-send-button tr disabled? send-message! pending?]]
         [:div.hidden.lg:flex.text-xs.text-gray-500
          (conversation/quick-send-shortcut-tip tr)]]))))


(defn- flash-on-mount
  "Wrapper that applies a background flash animation on mount.
   Starts with bg-ui-blue-10, transitions to transparent over 500ms."
  [_content]
  (let [*flash? (r/atom true)]
    (r/create-class
      {:component-did-mount
       (fn [_]
         (js/setTimeout #(reset! *flash? false) 500))
       :reagent-render
       (fn [content]
         [:div.transition-colors.duration-500.rounded
          {:class (if @*flash? "bg-ui-blue-10" "bg-ui-white")}
          content])})))


(defn- creator-role-label
  "Returns role label for a message creator. Authority users get 'Authority',
   others get their party role from available recipients."
  [msg tr creator-id]
  (if (:conversation/by-authority? msg)
    (tr [:conversation/authority])
    (some-> (<sub [::conversation/available-direct-message-recipients])
            (get creator-id)
            :roles
            (->> (format-roles tr))
            not-empty)))


(defn- thread-message-item [msg tr thread-id highlighted-message-ids last?]
  (let [current-user-id  (<sub [::current-user-id])
        is-admin?        (<sub [::is-admin?])
        can-participate? (<sub [::can-participate?])
        inactive?        (<sub [::conversation/direct-conversation-inactive?])
        date-time-fn     (<sub [::date-util/date-time-fn])
        reply-open?      (= thread-id (<sub [::reply-thread-id]))
        creator-id       (some-> msg :entity/creator :db/id)
        my-message?      (= current-user-id creator-id)
        msg-id           (:db/id msg)
        unread?          (and (some? msg-id)
                              (not my-message?)
                              (or (contains? highlighted-message-ids msg-id)
                                  (not (<sub [::conversation/msg-seen? msg-id]))))
        author           (if my-message?
                           (tr [:conversation/you])
                           (some-> msg :entity/creator user-model/full-name))
        role-label       (when-not my-message?
                           (creator-role-label msg tr creator-id))
        timestamp        (some-> msg :entity/created date-time-fn)
        deleted?         (du/in-state? :conversation.state/deleted msg)
        text             (some-> msg :conversation/text)
        just-sent?       (and my-message?
                              (when-let [created (:entity/created msg)]
                                (> (.getTime created)
                                   (- (.getTime (js/Date.)) 3000))))
        is-private?      (<sub [::is-private?])
        participating?   (<sub [::participating?])
        message-el       [:div.relative.rounded.border.border-solid.border-gray-300.p-2.bg-ui-white
                          [:div.text-sm.whitespace-pre-wrap.px-2.pt-2.pb-1.leading-snug
                           (if deleted?
                             [:span.text-ui-dark-gray (tr [:conversation/deleted])]
                             [:span.text-ui-night-sky-100 text])]
                          [:div.flex.gap-2
                           (when (and can-participate? (or participating? (not is-private?)) (not inactive?))
                             [ui/button {:kind       :tertiary
                                         :size       :small
                                         :type       :button
                                         :on-click   #(do
                                                        (>evt [::set-new-message-text thread-id ""])
                                                        (if reply-open?
                                                          (>evt [::set-reply-thread-id nil])
                                                          (>evt [::set-reply-thread-id thread-id])))
                                         :class-name "gap-2 !px-2 !py-1"}
                              (tr [:conversation/reply])
                              [:> Reply16]])
                           (when (and is-admin? (not deleted?))
                             [ui/modal-confirmation-button
                              {:id                  (str "remove-message-" msg-id)
                               :tr                  tr
                               :trigger-button-text (tr [:conversation/delete])
                               :trigger-button-opts {:kind       :ghost
                                                     :size       :small
                                                     :icon       i/trash
                                                     :class-name "!px-2 !py-1 [&_.bx--btn__icon]:ml-0"}
                               :dangerous?          true
                               :on-yes-click        #(>evt [::conversation/delete-message msg-id])}
                              [:span (tr [:conversation/delete-confirm]
                                         [(some-> msg :entity/created date-time-fn)
                                          (some-> msg :entity/creator user-model/full-name)])]])]]]
    [:div.py-1.pl-5.first:pl-0
     (cond-> {} last? (assoc :data-last-thread-message true))
     [:div.flex.items-end.justify-between.gap-2.py-2
      [:div.flex.flex-col
       [:span.font-bold.text-xs.text-ui-night-sky-100 author]
       (when role-label
         [:span.text-xs.text-ui-night-sky-70 role-label])]
      [:div.flex.items-center.gap-1.shrink-0
       [:div.text-xs.text-ui-night-sky-70 timestamp]
       (when unread?
         [unread-dot])]]
     (if just-sent?
       [flash-on-mount message-el]
       message-el)]))


(defn- conversation-thread-item
  "Individual conversation thread card - renders the full message thread"
  [thread tr workspace-id highlighted-message-ids *scroll-ref]
  (let [messages    (sort-by :entity/created (:entity/_parent thread))
        thread-id   (:db/id thread)
        last-msg-id (:db/id (last messages))
        reply-open? (= thread-id (<sub [::reply-thread-id]))]
    [:div.flex.flex-col.gap-2.mb-2
     {:data-thread-id (str thread-id)}
     [:div.flex.flex-col.gap-1
      (for-frag [msg messages]
        ^{:key (:db/id msg)}
        [thread-message-item msg tr thread-id highlighted-message-ids
         (= (:db/id msg) last-msg-id)])
      (when reply-open?
        [reply-input tr thread-id workspace-id *scroll-ref])]]))


(defn- content-switcher
  "Content switcher for Public/Private messages"
  [tr]
  (let [visibility          (<sub [::message-visibility])
        has-unread-private? (<sub [::has-unread-private?])]
    [ui-content-switcher/content-switcher
     {:items     [{:value :public}
                  {:value :private :notification? has-unread-private?}]
      :current   visibility
      :on-change #(>evt [::set-message-visibility %])
      :val->str  (fn [item]
                   (case item
                     :public (tr [:conversation/public])
                     :private (tr [:conversation/private])
                     ""))}]))


(defn- format-dm-recipients
  "Format recipient names for display, excluding current user"
  [current-user participants]
  (->> participants
       (remove #(= (:db/id %) (:db/id current-user)))
       (map user-model/full-name)
       (str/join ", ")))


(defn- direct-conversation-item
  "Clickable row for a DM conversation in the categorized list"
  [user {:keys [participants new-message-count]}]
  [:a.border.border-solid.border-gray-400.mb-2.p-2.rounded.h-10.flex.flex-row.items-center.hover:bg-ui-violet-10.no-underline.cursor-pointer
   {:on-click  #(>evt [::conversation/set-direct-message-participants (->> participants (map :db/id) set)])
    :tab-index 0
    :role      "button"}
   [:span.font-bold.text-sm.text-ui-night-sky-100.truncate.flex-1 (format-dm-recipients user participants)]
   (when (pos? new-message-count)
     [:div.ml-2 [unread-dot]])])


(defn- direct-conversation-category
  "Section header + list of DM conversations for a category"
  [user title conversations]
  (when (seq conversations)
    [:div
     [:h3.font-bold.text-sm.text-ui-night-sky-100.py-2 title]
     (for-frag [conversation conversations]
       ^{:key (str/join "-" (map :db/id (:participants conversation)))}
       [direct-conversation-item user conversation])]))


(defn- direct-conversation-list
  "Categorized list of all DM conversations"
  [tr]
  (let [user                      (<sub [:session/user])
        conversations-by-category (<sub [::conversation/direct-conversations-by-category])
        all-direct-threads        (<sub [::conversation/workspace-conversation-threads true])
        can-participate?          (<sub [::can-participate?])]
    [:div.flex-1.min-h-0.overflow-y-auto.overscroll-contain.-my-2.py-6
     [:div.flex.flex-col.gap-1
      (when can-participate?
        [recipient-selector tr])
      (if (empty? all-direct-threads)
        [:div.text-center.py-8.text-ui-night-sky-70
         (tr [:conversation/no-direct-conversations])]
        [:<>
         [direct-conversation-category user (tr [:conversation/new-messages])
          (:new-messages conversations-by-category)]
         [direct-conversation-category user (tr [:conversation/current-discussions])
          (:other conversations-by-category)]
         [direct-conversation-category user (tr [:conversation/inactive-discussions])
          (:inactive conversations-by-category)]
         [direct-conversation-category user (tr [:conversation/not-participating])
          (:not-participating conversations-by-category)]])]]))


(defn- direct-conversation-header
  "Header for a selected DM conversation: back button + recipient info"
  [tr]
  (let [user                 (<sub [:session/user])
        participants         (<sub [::conversation/selected-direct-message-participants])
        recipient-id         (some->> participants (remove #(= % (:db/id user))) first)
        available-recipients (<sub [::conversation/available-direct-message-recipients])
        recipient            (or (get available-recipients recipient-id)
                                 (<sub [::conversation/recipient-info-from-threads recipient-id]))
        recipient-name       (some-> recipient :user user-model/full-name)
        recipient-roles      (when-let [roles (not-empty (:roles recipient))]
                               (if (contains? roles :conversation/authority)
                                 (tr [:conversation/authority])
                                 (format-roles tr roles)))]
    [:div.flex.flex-row.lg:flex-col.gap-4.lg:gap-0.border-b.border-solid.border-gray-400.-mt-4.lg:mt-0.justify-between
     [panel-back-button #(>evt [::conversation/set-direct-message-participants nil]) (tr [:messages/all-private-discussions])]
     ;; Recipient info
     [:div.flex.flex-row.items-center.py-3.text-black.gap-1
      [:> User24]
      [:div.flex.flex-col
       [:span.font-bold.text-sm.text-ui-night-sky-100 recipient-name]
       (when recipient-roles
         [:span.text-xs.text-ui-night-sky-70 recipient-roles])]]]))


(defn- rendered-conversation-ids
  [threads]
  (->> threads
       (mapcat :entity/_parent)
       (keep :db/id)
       (into #{})))


(defn- unseen-user-ids
  [msg]
  (let [unseen-users (:conversation/unseen-users msg)]
    (cond
      (set? unseen-users)
      unseen-users

      (sequential? unseen-users)
      (->> unseen-users
           (keep (fn [u]
                   (cond
                     (number? u) u
                     (map? u) (:db/id u))))
           set)

      :else
      #{})))


(defn- mark-rendered-messages-seen!
  [workspace-id threads current-user-id]
  (when workspace-id
    (>evt [::mark-rendered-messages-seen
           workspace-id
           (rendered-conversation-ids threads)
           current-user-id])))


(defn- total-message-count
  "Count total messages across all threads for scroll change detection."
  [threads]
  (transduce (map #(count (:entity/_parent %))) + 0 threads))


(defn- workspace-conversations-list-with-seen [_ _ _ _ _ _ _]
  (let [*scroll-ref       (r/atom nil)
        *loaded?          (atom false)
        scroll-to!        (fn [smooth? target-top-fn]
                            (when-let [scroll-el @*scroll-ref]
                              (when-let [top (target-top-fn scroll-el)]
                                (.scrollTo scroll-el #js {:top      top
                                                          :behavior (if smooth? "smooth" "instant")})
                                nil)))
        scroll-to-end!    (fn [smooth?]
                            (scroll-to! smooth? (fn [scroll-el] (.-scrollHeight scroll-el))))
        scroll-to-thread! (fn [thread-id smooth?]
                            (scroll-to! smooth?
                                        (fn [scroll-el]
                                          (when-let [thread-el (.querySelector scroll-el
                                                                               (str "[data-thread-id=\"" thread-id "\"]"))]
                                            (let [last-msg      (.querySelector thread-el "[data-last-thread-message]")
                                                  target        (or last-msg thread-el)
                                                  target-top    (- (.-offsetTop target) (.-offsetTop scroll-el))
                                                  target-center (+ target-top (/ (.-offsetHeight target) 2))
                                                  viewport-h    (.-clientHeight scroll-el)]
                                              (max 0 (- target-center (/ viewport-h 2))))))))]
    (r/create-class
      {:display-name
       "messages-panel-workspace-conversations-list"
       :component-did-mount
       (fn [this]
         (let [[_ _tr threads _loading? _is-private? workspace-id current-user-id] (r/argv this)]
           (mark-rendered-messages-seen! workspace-id threads current-user-id)
           (when (seq threads)
             (reset! *loaded? true))
           (scroll-to-end! false)))
       :component-did-update
       (fn [this old-argv]
         (let [[_ _tr threads _loading? _is-private? workspace-id current-user-id
                replied-thread-id]                                                (r/argv this)
               [_ _ old-threads]                                                  old-argv]
           (mark-rendered-messages-seen! workspace-id threads current-user-id)
           (when (or (not= (count threads) (count old-threads))
                     (not= (total-message-count threads) (total-message-count old-threads)))
             (if (and @*loaded? replied-thread-id)
               (do (scroll-to-thread! replied-thread-id true)
                   (>evt [::set-last-replied-thread-id nil]))
               (let [smooth? @*loaded?]
                 (reset! *loaded? true)
                 (scroll-to-end! smooth?))))))
       :component-will-unmount
       (fn [_]
         (>evt [::reset-mark-state]))
       :reagent-render
       (fn [tr threads loading? is-private? workspace-id _current-user-id _replied-thread-id]
         (let [highlighted-message-ids (<sub [::highlighted-message-ids])]
           [:div.flex-1.min-h-0.overflow-y-auto.overscroll-contain.-my-2.py-6.pr-2
            {:ref #(reset! *scroll-ref %)}
            [:div.flex.flex-col.gap-6
             (cond
               loading?
               [:div.flex.items-center.justify-center.py-8
                (cc/inline-loading {:description (tr [:messages/loading])})]

               (empty? threads)
               [:div.text-center.py-8.text-ui-night-sky-70
                (if is-private?
                  (tr [:conversation/no-direct-conversations])
                  (tr [:conversation/application-has-no-messages]))]

               :else
               (let [threads-by-phases (group-by :conversation-thread/workspace-state threads)]
                 (for-frag [phase workspace-model/workspace-states]
                   (let [phase-threads (get threads-by-phases phase)]
                     (when (seq phase-threads)
                       [:<>
                        [phase-label (tr [phase])]
                        (for-frag [thread phase-threads]
                          ^{:key (:db/id thread)}
                          [conversation-thread-item thread tr workspace-id
                           highlighted-message-ids *scroll-ref])])))))]]))})))


(defn- workspace-conversations-list
  "List of conversation threads for a workspace"
  [tr]
  (let [is-private?       (<sub [::is-private?])
        participants      (<sub [::conversation/selected-direct-message-participants])
        threads           (if (and is-private? (some? participants))
                            (<sub [::direct-conversation-threads])
                            (<sub [::workspace-threads]))
        loading?          (<sub [::loading?])
        workspace-id      (<sub [::conversation/selected-workspace-id])
        current-user-id   (<sub [::current-user-id])
        replied-thread-id (<sub [::last-replied-thread-id])]
    ^{:key (str workspace-id "-" is-private? "-" participants)}
    [workspace-conversations-list-with-seen tr threads loading? is-private? workspace-id current-user-id
     replied-thread-id]))


(defn- format-roles
  "Format roles for display in recipient selector.

   `roles` is a heterogeneous set of either party-role entities or role
   keywords. The same underlying role can appear more than once (e.g. the same
   role as distinct party-role entities across phases/parties), so dedupe by the
   resolved label before joining to avoid e.g. \"Applicant, Applicant\"."
  [tr roles]
  (->> roles
       (map #(or (party-common/party-role-label %)
                 (tr [%])))
       (remove str/blank?)
       distinct
       (sort-by (fnil str/lower-case ""))
       (str/join ", ")))


(defn- recipient-selector
  "Dropdown to select recipient for private messages"
  [tr]
  (let [available-recipients  (<sub [::conversation/available-direct-message-recipient-options])
        participants          (<sub [::conversation/selected-direct-message-participants])
        user                  (<sub [:session/user])
        lang                  (<sub [:language])
        user-id               (:db/id user)
        selected-recipient-id (some->> participants (remove #(= % user-id)) first)
        id->label             (into {}
                                    (map (partial conversation/recipient-label lang tr))
                                    available-recipients)
        recipient-ids         (mapv first available-recipients)]
    [form-stack-div
     [form-field-div
      [ui-select/select {:id           "messages-panel-recipient"
                         :class-name   "w-full"
                         :allow-empty? true
                         :hide-label   true
                         :label-text   (tr [:conversation/select-recipient])
                         :empty-text   (tr [:conversation/select-recipient])
                         :values       recipient-ids
                         :value        selected-recipient-id
                         :on-change    (fn [recipient-id]
                                         (if recipient-id
                                           (>evt [::conversation/set-direct-message-participants #{user-id recipient-id}])
                                           (>evt [::conversation/set-direct-message-participants nil])))
                         :val->str     #(get id->label % "")}]]]))


(defn- message-input
  "Message input field with send button"
  [tr workspace-id participants recipients is-private?]
  (let [thread-key    (or participants workspace-id)
        message       (<sub [::new-message-text thread-key])
        pending?      (<sub [::conversation/message-send-pending?])
        inactive?     (<sub [::conversation/direct-conversation-inactive?])
        disabled?     (or (str/blank? message)
                          pending?
                          inactive?
                          (and is-private?
                               (not (seq recipients))))
        textarea-id   (str "messages-panel-message-input-" (or workspace-id "none"))
        send-message! (fn [e]
                        (.preventDefault e)
                        (when-not pending?
                          (>evt [::conversation/add-new-message message workspace-id nil
                                 (when is-private? (-> recipients sort vec)) nil])
                          (>evt [::set-new-message-text thread-key ""])
                          (reset-textarea-height! textarea-id)))]
    [:div.flex.flex-col.gap-1
     [:div.relative.flex.items-start.gap-2
      ;; Textarea
      [:div.textarea-container.self-stretch.flex-1
       [ui/text-area {:id          textarea-id
                      :label-text  (tr [:conversation/write-new-message-here])
                      :hide-label  true
                      :rows        1
                      :value       (or message "")
                      :placeholder (tr [:conversation/write-new-message-here])
                      :light       true
                      :class       "self-stretch"
                      :input-class (str "flex-1 !min-h-11 !pt-2.5 !px-2 !text-ui-night-sky-100"
                                        " placeholder:!text-ui-night-sky-100 text-base"
                                        " border border-solid border-gray-400 rounded"
                                        " resize-none overflow-y-auto max-h-[7.5rem]")
                      :on-change   #(>evt [::set-new-message-text thread-key (tval %)])
                      :on-key-up   auto-resize-textarea
                      :on-blur     auto-resize-textarea
                      :on-key-down (conversation/get-quick-send-handler send-message! disabled?)
                      :key         workspace-id}]]
      ;; Send button
      [panel-send-button tr disabled? send-message! pending?]]
     [:div.hidden.lg:flex.text-xs.text-gray-500
      (conversation/quick-send-shortcut-tip tr)]]))


(defn- conversation-status-message
  "Status message shown when user cannot send messages in a conversation"
  [tr-key tr]
  [:div.shrink-0.bg-ui-white.-mx-4.-mb-4.px-4.pb-4.flex.flex-col.gap-2
   [:div.h-px.bg-gray-400]
   [:div.py-2.px-2.text-sm.text-ui-night-sky-70.leading-normal
    (tr [tr-key])]])


(defn- message-input-or-status [tr]
  (let [workspace-id     (<sub [::conversation/selected-workspace-id])
        participants     (<sub [::conversation/selected-direct-message-participants])
        recipients       (<sub [::conversation/selected-direct-message-recipients])
        can-participate? (<sub [::can-participate?])
        inactive?        (<sub [::conversation/direct-conversation-inactive?])
        visibility       (<sub [::message-visibility])
        is-private?      (= visibility :private)
        user             (<sub [:session/user])
        participating?   (contains? (set participants) (:db/id user))]
    (cond
      (not can-participate?)
      [conversation-status-message :conversation/read-only-description tr]

      (and is-private? (not participating?))
      [conversation-status-message :conversation/not-participating-description tr]

      inactive?
      [conversation-status-message :conversation/inactive-description tr]

      :else
      [:div.shrink-0.bg-ui-white.-mx-4.-mb-4.px-4.pb-4.flex.flex-col.gap-2
       [:div.h-px.bg-gray-400]
       ;; Message input
       [message-input tr workspace-id participants recipients is-private?]])))


(defn- slide-in-container [props & children]
  [:div.flex.flex-col.flex-1.min-h-0.animate-overflow-hidden-200
   (into [:div.flex.flex-col.flex-1.min-h-0.animate-appear-slide-in-right-200
          (or props {})]
         children)])


(defn- workspace-private-messages-view
  "View showing private conversations with a selected recipient user within selected workspace"
  [tr]
  [slide-in-container
   {:class "gap-2"}
   [direct-conversation-header tr]
   [workspace-conversations-list tr]
   [message-input-or-status tr]])


(defn workspace-messages-view
  "View showing messages for a specific workspace"
  [tr]
  (let [selected-ws  (<sub [::conversation/selected-workspace-list-item])
        workspace    (:workspace selected-ws)
        fullscreen?  (<sub [::fullscreen?])
        visibility   (<sub [::message-visibility])
        is-private?  (= visibility :private)
        participants (<sub [::conversation/selected-direct-message-participants])
        dm-selected? (and is-private? (some? participants))]
    [slide-in-container
     {:class "gap-2"}
     (when workspace
       (if dm-selected?
         [workspace-private-messages-view tr]
         [:<>
          ;; In the full-screen view the workspace address/details are shown in
          ;; the summary sidebar instead of a header above the messages.
          (when-not fullscreen?
            [workspace-header tr workspace])
          ;; Content switcher (Public/Private tabs)
          [content-switcher tr]
          (if is-private?
            ;; Private tab with no participant selected: show categorized DM list
            [direct-conversation-list tr]
            [:<>
             [workspace-conversations-list tr]
             [message-input-or-status tr]])]))]))


(defn messages-panel [tr]
  (let [open?     (<sub [::panel-open?])
        view-mode (<sub [::view-mode])]
    [side-panel/side-panel {:panel-open? open? :panel-id :messages}
     ^{:key "messages-panel-header"}
     (when-not (= view-mode :workspace-messages)
       [:div.flex.flex-row.items-center.justify-between.gap-2
        [side-panel/title (tr [:navigation/messages])]
        [:div {:class "mt-1.5 -mb-1"} [open-full-screen-button tr]]])
     ^{:key "messages-panel-body"}
     [:div.flex.flex-col.flex-1.min-h-0
      (when open?
        (case view-mode
          :workspace-messages [workspace-messages-view tr]
          [all-messages-view tr]))]]))


(defn- full-screen-empty-state
  "Placeholder shown in the right pane when no workspace is selected."
  [tr]
  [:div.flex.flex-1.items-center.justify-center.text-ui-night-sky-70.text-sm.p-8
   (tr [:conversation/please-select-application])])


(defn- summary-map
  "Small non-interactive map showing the workspace location. Rendered only when
   a map center is available; layers require the group map config to be loaded."
  [_workspace]
  (r/create-class
    {:display-name "messages-panel-summary-map"
     :component-did-mount
     (fn [this]
       (>evt [::ensure-workspace-map-config (-> this r/argv second)]))
     :component-did-update
     (fn [this [_ prev-workspace]]
       (let [workspace (-> this r/argv second)]
         (when (not= (:db/id prev-workspace) (:db/id workspace))
           (>evt [::ensure-workspace-map-config workspace]))))
     :reagent-render
     (fn [workspace]
       (let [center     (<sub [::selected-workspace-map-center])
             layers     (<sub [::selected-workspace-map-layers])
             ws-shape   (-> workspace :entity/location :location/shape)
             prop-shape (-> workspace :workspace/primary-property :entity/location :location/shape)]
         (when center
           [:div.relative.w-full.rounded.overflow-hidden.border.border-solid.border-gray-300.h-40.shrink-0
            [workspace-map/workspace-map
             (p/assoc-if
               {:center            center
                :zoom              13
                :layers            (or layers [])
                :extended?         true
                :primaryMapMarkers [center]
                :hiddenTools       (concat workspace-map/default-hidden-tools
                                           [workspace-map/default-tool
                                            workspace-map/layer-tool
                                            workspace-map/measure-tool])
                :hiddenDrawTools   workspace-map/default-hidden-draw-tools}
               :propertyGeometries (when prop-shape
                                     {:primaryPropertyGeometry (property-model/geojson-feature prop-shape)})
               :workspaceGeometries (when ws-shape
                                      (property-model/geojson-feature-collection
                                        [(property-model/geojson-feature ws-shape)])))]])))}))


(defn- summary-detail
  "A single labelled detail row in the summary sidebar."
  [label value]
  (when value
    [:div.flex.flex-col.gap-0.5
     [:span.text-xs.font-bold.text-ui-night-sky-70.uppercase.tracking-wide label]
     [:div.text-sm.text-ui-night-sky-100.break-words value]]))


(defn- summary-applicant-card
  "Small applicant info card (authority-only)."
  [tr applicants]
  (when (seq applicants)
    [:div.flex.flex-col.gap-1
     [:span.text-xs.font-bold.text-ui-night-sky-70.uppercase.tracking-wide (tr [:role/applicant])]
     (for-frag [name applicants]
       [:div.flex.flex-row.items-center.gap-2.rounded.border.border-solid.border-gray-300.bg-gray-100.p-2
        [:div.shrink-0.text-ui-night-sky-70 [:> User24]]
        [:span.text-sm.text-ui-night-sky-100.break-words name]])]))


(defn- workspace-summary-sidebar
  "Narrow sidebar showing summary information about the selected workspace so
   the user can visualize which workspace the conversation belongs to. A small
   map is shown above textual details. Some details (assignee, applicant) are
   only relevant to and visible for authority users."
  [tr]
  (let [workspace    (<sub [::selected-workspace])
        lang         (<sub [:language])
        authority?   (<sub [::selected-workspace-authority?])
        applicants   (<sub [::selected-workspace-applicants])
        category     (<sub [::selected-workspace-category])
        work-type    (<sub [::selected-workspace-work-type])
        work-target  (<sub [::selected-workspace-work-target])
        permit-type  (:workspace/permit-type workspace)
        state        (:domain/state workspace)
        address      (or (workspace-model/full-address workspace)
                         (:workspace/address workspace)
                         (:workspace/id workspace))
        municipality (municipality-model/common-name-province
                       (:workspace/municipality workspace) tr lang)
        case-id      (workspace-model/case-id workspace)
        assignee     (some-> (:workspace/assignee workspace) user-model/full-name)]
    [:aside.flex.flex-col.gap-4.shrink-0.overflow-y-auto.overscroll-contain.px-6.py-4
     {:style {:width "328px"}}
     [summary-map workspace]
     [:div.flex.flex-col.gap-4
      [ui/button {:kind          :secondary
                  :size          :field
                  :icon          i/arrow-right
                  :icon-position :right
                  :class-name    "w-full"
                  :href          (href/href :view/workspace {:workspace-id (:workspace/id workspace)})}
       (tr [:messages/go-to-workspace])]
      (when permit-type
        [:div.flex.flex-row.items-center.gap-2
         (when-let [icon (ui/permit-type-icon permit-type)]
           [:div.border-2.border-solid.rounded-full.p-1.flex-shrink-0
            [:> icon {:class-name "block"}]])
         [:span.font-bold.text-ui-night-sky-100 (tr [permit-type])]])
      [summary-detail (tr [:messages/workspace-status]) (when state (tr [state]))]
      [summary-detail (tr [:application/address]) address]
      (when municipality
        [summary-detail (tr [:workspace/municipality]) municipality])
      (when case-id
        [summary-detail (tr [:workspace/municipal-case-id]) case-id])
      (when category
        [summary-detail (tr [:workspace/category]) category])
      (when work-type
        [summary-detail (tr [:application/work-type]) work-type])
      (when work-target
        [summary-detail (tr [:application/work-target]) work-target])
      (when authority?
        [:<>
         [summary-detail (tr [:workspace/assigned-to]) (or assignee (tr [:unassigned]))]
         [summary-applicant-card tr applicants]])]]))


(defn full-screen-messages-view
  "Full-screen messages view: a two-column layout that shows the workspace
   list (same as the messages side panel's all-messages view) on the left and
   the selected workspace's messages on the right. Styles are shared with the
   side panel; the right pane is wider to make room for full conversations."
  [_]
  (if-not (ff/enabled? :feature-flag.id/full-screen-messages)
    [not-found/not-found-view]
    (let [tr            (<sub [:tr])
          ;; Reuse the same positioning logic as the side panel: the sticky
          ;; header's visible bottom (which already accounts for a possible
          ;; env-banner) is subtracted from the viewport height so the view
          ;; exactly fills the space below the header. This prevents the whole
          ;; page body from scrolling (and the header from being scrolled out of
          ;; view); scrolling is confined to the two inner columns instead.
          header-bottom (<sub [::viewport-observer/visible-bottom :sticky-header])
          viewport-h    (<sub [::viewport-observer/viewport-height])
          height        (max 0 (- viewport-h header-bottom))
          selected-ws   (<sub [::conversation/selected-workspace-list-item])
          panel-open?   (some? (<sub [::panels/open-panel]))]
      [:div.flex.flex-col.min-h-0.bg-ui-white.overflow-hidden
       {:style {:height (str height "px")}}
       ;; No padding on this wrapper: paddings live inside each scrollable column
       ;; so that scrollbars reach the top/bottom (and outer) edges of the view
       ;; instead of floating inside a padded box.
       [:div.flex.flex-row.flex-1.min-h-0.max-w-screen-2xl.w-full.mx-auto
        ;; Left column: workspace list (same width as the side panel, plus its
        ;; padding). The scrollbar spans the full column height; the inner
        ;; padding keeps a gap between the scrollbar/edges and the content items.
        [:div.flex.flex-col.min-h-0.shrink-0.overflow-y-auto.overscroll-contain.pl-6.pr-4.py-4
         {:style {:width "384px"}}
         [:h1.font-bold.text-ui-night-sky-100.text-lg.mb-2 (tr [:navigation/messages])]
         [all-messages-view tr]]
        ;; Divider
        [:div.w-px.bg-gray-300.self-stretch]
        ;; Right column: workspace messages (wider), plus a summary sidebar.
        ;; The messages column uses p-4 to match the side panel's padding, so the
        ;; components that bleed to the edges (message input separator, message
        ;; list) align and their scrollbars reach the column edges.
        (if (:workspace selected-ws)
          [:<>
           [:div.flex.flex-col.flex-1.min-h-0.min-w-0.p-4
            [workspace-messages-view tr]]
           [:div.w-px.bg-gray-300.self-stretch]
           ;; Show workspace summary sidebar only when none of the global side
           ;; panels is visible.
           (when-not panel-open?
             [workspace-summary-sidebar tr])]
          [:div.flex.flex-col.flex-1.min-h-0.min-w-0.p-4
           [full-screen-empty-state tr]])]])))
